'use client'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import Autoplay from 'embla-carousel-autoplay'
import { useTranslations } from 'next-intl'
import Image from 'next/image'

const AUTOPLAY_OPTIONS = {
  delay: 3000,
  stopOnInteraction: false,
  stopOnMouseEnter: true,
}

type TheMartianCard = {
  key: string
  image: string
  imageClassName?: string
}

type TheMartianCardSliderProps = {
  /** Namespace chứa `<key>.title` và `<key>.description` */
  translationKey: string
  items: TheMartianCard[]
  className?: string
}

/** Hàng card ảnh + tiêu đề (dùng cho "Tính năng" và "5 trụ cột"), loop + tự chạy ở mọi breakpoint */
export const TheMartianCardSlider = ({
  translationKey,
  items,
  className,
}: TheMartianCardSliderProps) => {
  const t = useTranslations(translationKey)
  // Embla chỉ loop khi tổng chiều rộng slide đủ lớn hơn khung (5 card desktop chưa đủ) → nhân đôi danh sách
  const loopItems = [...items, ...items]

  return (
    <section className={cn('px-5 py-10 lg:px-15 lg:py-20', className)}>
      {/* Figma: card tràn ra mép phải màn hình */}
      <Carousel
        opts={{ align: 'start', loop: true }}
        plugins={[Autoplay(AUTOPLAY_OPTIONS)]}
        className='-mr-5 lg:-mr-15'
      >
        <CarouselContent className='-ml-5'>
          {loopItems.map((item, index) => (
            <CarouselItem
              key={`${item.key}-${index}`}
              // Figma: mobile card 210px, desktop 4 card/khung 1800 (435px) — trừ phần tràn lề phải 60px
              // Card không click được → bỏ cursor-pointer mặc định của CarouselItem
              className='basis-[230px] cursor-default pl-5 lg:basis-[calc((100%-60px)/4)]'
            >
              <div className='relative flex aspect-[435/558] flex-col justify-end gap-1 overflow-hidden rounded-2xl px-2 py-3 lg:px-5 lg:py-6'>
                <Image
                  src={ImageHelper.getImageUrlCdn(item.image)}
                  alt={t(`${item.key}.title`)}
                  fill
                  sizes='(min-width: 1024px) 23vw, 210px'
                  className={cn('object-cover', item.imageClassName)}
                />
                <div className='absolute inset-x-0 bottom-0 h-[56%] bg-gradient-to-b from-transparent to-black/75 lg:h-[38%] lg:to-black/65' />
                <p className='relative font-criteria text-lg font-medium uppercase text-white lg:text-2xl'>
                  {t(`${item.key}.title`)}
                </p>
                <p className='relative text-xs text-neutral-50 lg:text-base'>
                  {t(`${item.key}.description`)}
                </p>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </section>
  )
}
